"use client";

// eslint-disable-next-line no-restricted-imports -- This wrapper centralizes useActiveOrganization
import { useAuthReady } from "@/hooks/auth/use-auth-ready";
// eslint-disable-next-line no-restricted-imports -- This wrapper centralizes useActiveOrganization
import {
  type ActiveOrganizationResult,
  useActiveOrganization,
} from "@/hooks/use-active-organization";

export interface OrganizationQueryOptions {
  /**
   * When false, skips checking the organization-context readiness flag from useActiveOrganization.
   * Use only if a hook can operate with a Clerk org before custom context is ready.
   */
  requireContextReady?: boolean;
}

export interface OrganizationQueryState {
  organizationId: string | null;
  isAuthReady: boolean;
  readiness: ActiveOrganizationResult["readiness"];
  source: ActiveOrganizationResult["source"];
  isOrganizationReady: boolean;
}

export interface OrganizationQueryGuardOptions
  extends OrganizationQueryOptions {
  /**
   * Whether consumers explicitly requested the query to run.
   * Defaults to true but still respects readiness + organization id.
   */
  enabled?: boolean;
  /**
   * Optional identifier that shows up in development logs for easier debugging.
   */
  debugKey?: string;
}

export interface OrganizationQueryGuardState extends OrganizationQueryState {
  /**
   * Final flag to pass to React Query or any async loader.
   */
  queryEnabled: boolean;
  /**
   * True when caller requested enabled=true but readiness blocked it.
   */
  blockedByContext: boolean;
  /**
   * True when caller explicitly disabled the query regardless of readiness.
   */
  disabledByCaller: boolean;
  /**
   * Optional identifier to aid debugging/logging.
   */
  debugKey?: string;
}

export function useOrganizationQuery(
  options: OrganizationQueryOptions = {},
): OrganizationQueryState {
  const activeOrganization = useActiveOrganization();
  const { isReady: isAuthReady } = useAuthReady();

  const organizationId = activeOrganization.organizationId;
  const contextReady =
    options.requireContextReady === false
      ? true
      : activeOrganization.isOrganizationReady;

  const isOrganizationReady = Boolean(
    isAuthReady && contextReady && organizationId,
  );

  return {
    organizationId,
    isAuthReady,
    readiness: activeOrganization.readiness,
    source: activeOrganization.source,
    isOrganizationReady,
  };
}

export function useOrganizationQueryGuard(
  options: OrganizationQueryGuardOptions = {},
): OrganizationQueryGuardState {
  const { enabled = true, debugKey, ...orgOptions } = options;
  const state = useOrganizationQuery(orgOptions);

  const queryEnabled = Boolean(
    enabled && state.isOrganizationReady && state.organizationId,
  );

  const disabledByCaller = !enabled;
  const blockedByContext = !disabledByCaller && !queryEnabled;

  return {
    ...state,
    queryEnabled,
    blockedByContext,
    disabledByCaller,
    debugKey,
  };
}
